import { useCallback, useMemo, useState } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Skeleton from "@/v2-ui/skeleton"
import { errorToast, toast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import TenantPrivacyApplyPresetDialog from "@/v2-backoffice-client/tenant/privacy/TenantPrivacyApplyPresetDialog"
import TenantPrivacyPolicyFooter from "@/v2-backoffice-client/tenant/privacy/TenantPrivacyPolicyFooter"
import TenantPrivacyPolicyHeader from "@/v2-backoffice-client/tenant/privacy/TenantPrivacyPolicyHeader"
import TenantPrivacyPolicyTable from "@/v2-backoffice-client/tenant/privacy/TenantPrivacyPolicyTable"
import TenantPrivacyPresetCards from "@/v2-backoffice-client/tenant/privacy/TenantPrivacyPresetCards"
import { useTenantPrivacyCatalogQuery } from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.catalog.query"
import { useTenantPrivacyStateQuery } from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.state.query"
import { useTenantPrivacyPresetApplyMutation } from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.applyPreset.mutation"
import { useTenantPrivacyPoliciesUpsertMutation } from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.upsert.mutation"
import { useTenantPrivacyResetMutation } from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.reset.mutation"
import {
  dirtyFieldCodes,
  policyFieldCodes,
  presetLabel,
} from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.utils"
import type {
  PolicyDraft,
  PolicyFieldKey,
  PrivacyPolicyRow,
  PrivacyPreset,
} from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.types"

export type TenantPrivacyEditorProps = {
  tenantId: number
}

// The team panel's Access Control tab: quick-choice presets on top, the
// per-source table under them. Table edits buffer in a sparse draft (only
// the cells the operator touched); which rows are dirty is decided at render
// against the current server rows. Every write — save, preset, reset —
// returns the full server state into the same cache entity, so what the tab
// shows after a mutation is exactly what was stored, never a local guess.
function TenantPrivacyEditor(props: TenantPrivacyEditorProps) {
  const { tenantId } = props

  const catalog = useTenantPrivacyCatalogQuery()
  const state = useTenantPrivacyStateQuery({ variables: { tenantId } })

  const [applyPresetMutation, { loading: isApplying, error: applyError }] =
    useTenantPrivacyPresetApplyMutation()
  errorToast(applyError, { title: "Failed to apply the preset" })

  const [upsertPolicies, { loading: isSaving, error: saveError }] =
    useTenantPrivacyPoliciesUpsertMutation()
  errorToast(saveError, { title: "Failed to save the policy" })

  const [resetPolicies, { loading: isResetting, error: resetError }] =
    useTenantPrivacyResetMutation()
  errorToast(resetError, { title: "Failed to reset the policy" })

  const [draft, setDraft] = useState<PolicyDraft>({})
  const [justification, setJustification] = useState("")
  const [presetToApply, setPresetToApply] = useState<PrivacyPreset | null>(null)

  const tenantState = state.data?.getTenantPrivateDataVisibilityState
  const policies = tenantState?.Policies

  const rows = useMemo<PrivacyPolicyRow[]>(
    () =>
      (policies ?? []).map((policy) => ({
        ...policy,
        edits: dirtyFieldCodes(policy, draft[policy.ExternalSourceCode]),
      })),
    [policies, draft],
  )
  const dirtyRows = rows.filter((row) => row.edits !== null)
  const isDirty = dirtyRows.length > 0

  // Stable so the table's memoised columns survive edits; the draft is read
  // through the updater, never closed over.
  const changeField = useCallback(
    (sourceCode: number, key: PolicyFieldKey, code: number) => {
      setDraft((current) => ({
        ...current,
        [sourceCode]: { ...current[sourceCode], [key]: code },
      }))
    },
    [],
  )

  function clearDraft() {
    setDraft({})
    setJustification("")
  }

  // errorPolicy is "all" app-wide, so GraphQL errors resolve (and the hook
  // error toasts above carry the reason); only network failures reject, and
  // those already reach Sentry through the Apollo error link. The catches
  // keep the draft so the operator can retry.
  async function handleSave() {
    try {
      const result = await upsertPolicies({
        variables: {
          input: {
            TenantId: tenantId,
            Justification: justification.trim() || null,
            Policies: dirtyRows.map((row) => ({
              ExternalSourceCode: row.ExternalSourceCode,
              ...policyFieldCodes(row),
              ...row.edits,
            })),
          },
        },
      })
      if (result.errors?.length) return

      const savedCount = dirtyRows.length
      clearDraft()
      toast({
        variant: "success",
        title: "Policy saved",
        description: `${savedCount} source${savedCount === 1 ? "" : "s"} updated.`,
      })
    } catch {
      // Surfaced by the hook error state above.
    }
  }

  async function handleApplyPreset(presetJustification: string | null) {
    if (!presetToApply) return

    try {
      const result = await applyPresetMutation({
        variables: {
          input: {
            TenantId: tenantId,
            PresetCode: presetToApply.Code,
            Justification: presetJustification,
          },
        },
      })
      if (result.errors?.length) return

      clearDraft()
      setPresetToApply(null)
      toast({
        variant: "success",
        title: "Preset applied",
        description: `${presetLabel(presetToApply)} now sets the visibility of every source.`,
      })
    } catch {
      // Surfaced by the hook error state above.
    }
  }

  async function handleReset() {
    try {
      const result = await resetPolicies({ variables: { tenantId } })
      if (result.errors?.length) return

      clearDraft()
      toast({ variant: "success", title: "Policy reset" })
    } catch {
      // Surfaced by the hook error state above.
    }
  }

  const isLoading = catalog.loading || state.loading
  const isBusy = isApplying || isSaving || isResetting
  const loadError = catalog.error ?? state.error

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-muted-foreground">
        Which personal-data fields (national id, name, date of birth, ...) this
        team&apos;s members can see, per data source. Until a policy is set,
        everything is hidden. Deceased persons are always shown in full,
        whatever the policy says, and every disclosure Fusion makes is logged.
      </p>

      {isLoading ? (
        <>
          <Skeleton className="h-6 w-80" />
          <div className="grid gap-3 sm:grid-cols-3">
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
          </div>
          <Skeleton className="h-72" />
        </>
      ) : !catalog.data || !tenantState ? (
        // With errorPolicy "all" the queries settle without data only when
        // they failed (an unknown tenant is not an error), so say so in place
        // and offer a way back — a toast would be gone in seconds and leave
        // an empty tab that reads as "nothing configured".
        <div className="flex flex-col items-start gap-2">
          <ErrorMessage
            className="px-0"
            error={
              loadError
                ? `Could not load the team's access-control settings: ${loadError.message}`
                : "Could not load the team's access-control settings."
            }
          />
          <Button
            type="button"
            size="xs"
            variant="outline"
            data-feature-code="UI_BackofficeTenantPrivacy_Editor_Retry"
            onClick={() => {
              void catalog.refetch()
              void state.refetch()
            }}
          >
            Try again
          </Button>
        </div>
      ) : (
        <>
          <TenantPrivacyPolicyHeader
            tenantId={tenantId}
            summary={tenantState.Summary}
            sourceCount={rows.length}
            isBusy={isBusy}
            isResetting={isResetting}
            onReset={handleReset}
          />

          <TenantPrivacyPresetCards
            presets={catalog.data.getPrivateDataVisibilityPresets}
            matchesPresetCode={tenantState.Summary.MatchesPresetCode}
            isBusy={isBusy}
            onApply={setPresetToApply}
          />

          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold">Per-source policy</h3>
            <TenantPrivacyPolicyTable
              rows={rows}
              fields={catalog.data.getPrivateDataFields}
              visibilityTypes={catalog.data.getPrivateDataVisibilityTypes}
              isBusy={isBusy}
              onChangeField={changeField}
            />
            <TenantPrivacyPolicyFooter
              dirtyCount={dirtyRows.length}
              justification={justification}
              onJustificationChange={setJustification}
              isSaving={isSaving}
              onSave={() => {
                void handleSave()
              }}
              onCancel={clearDraft}
            />
          </section>

          <TenantPrivacyApplyPresetDialog
            preset={presetToApply}
            hasUnsavedEdits={isDirty}
            isApplying={isApplying}
            onClose={() => {
              setPresetToApply(null)
            }}
            onConfirm={(presetJustification) => {
              void handleApplyPreset(presetJustification)
            }}
          />
        </>
      )}
    </div>
  )
}

export default TenantPrivacyEditor
